// src/components/Invoice/InvoiceList.tsx

import React, { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInvoicesByDateRange } from "../../firebase/invoice/getInvoices";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	TextField,
	Button,
	Box
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HomeIcon from "@mui/icons-material/Home";
import { InvoiceData, LineItem } from "../../models/invoice";
import { formatDate } from "../../utils/calculations";

export const InvoiceList = () => {
	const navigate = useNavigate();
	const [invoices, setInvoices] = useState<{ [key: string]: InvoiceData }>(
		{}
	);
	const [loading, setLoading] = useState(true);
	const currentDate = new Date().toLocaleDateString("en-CA");
	const [startDate, setStartDate] = useState<string>(currentDate);
	const [endDate, setEndDate] = useState<string>(currentDate);

	useEffect(() => {
		const fetchInvoices = async () => {
			try {
				const data = await getInvoicesByDateRange(startDate, endDate);
				setInvoices(data);
			} catch (err) {
				console.error("Failed to load invoices:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchInvoices();
	}, [endDate, startDate]);

	const renderSearchInvoices = (): JSX.Element => {
		return (
			<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
						gap: 1
					}}
				>
					<Typography variant="h5">
						Search Invoices by Date Range
					</Typography>
					<Button
						variant="contained"
						startIcon={<HomeIcon />}
						onClick={() => navigate("/")}
					>
						Go to Home
					</Button>
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						gap: "1rem",
						marginBottom: "1rem",
						alignItems: { xs: "stretch", md: "center" }
					}}
				>
					<TextField
						label="Start Date"
						type="date"
						value={startDate}
						onChange={(e) => {
							setStartDate(e.target.value);
							console.log("Start Date", e.target.value);
						}}
						InputLabelProps={{ shrink: true }}
						size="small"
						fullWidth={true}
					/>
					<TextField
						label="End Date"
						type="date"
						value={endDate}
						onChange={(e) => {
							setEndDate(e.target.value);
							console.log("End Date", e.target.value);
						}}
						InputLabelProps={{ shrink: true }}
						fullWidth={true}
						size="small"
					/>
					<Button
						variant="contained"
						size="medium"
						sx={{
							width: { xs: "100%", md: "auto" },
							height: { md: "40px" }
						}}
						onClick={async () => {
							if (!startDate || !endDate) {
								alert(
									"Please select both start and end dates."
								);
								return;
							}
							if (startDate > endDate) {
								alert("Start date cannot be after end date.");
								return;
							}
							const data = await getInvoicesByDateRange(
								startDate,
								endDate
							);
							setInvoices(data);
						}}
					>
						Search
					</Button>
				</Box>
			</Paper>
		);
	};

	if (loading) return <p>Searching invoices...</p>;
	if (Object.keys(invoices).length === 0) {
		return (
			<>
				{renderSearchInvoices()}
				<p>No invoices found.</p>
			</>
		);
	}

	// calculate total amount for each invoice
	const calculateTotal = (items: LineItem[]) => {
		return items.reduce((total, item) => total + item.sellPrice, 0);
	};

	return (
		<>
			{renderSearchInvoices()}
			<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
				<Typography variant="h5" gutterBottom>
					Invoice History
				</Typography>

				<TableContainer component={Paper}>
					<Table
						sx={{
							"& .MuiTableCell-root": {
								border: "1px solid rgba(224, 224, 224, 1)"
							},
							"& .MuiTableCell-head": {
								fontWeight: "bold"
							}
						}}
					>
						<TableHead>
							<TableRow>
								<TableCell>Invoice Number</TableCell>
								<TableCell>Customer</TableCell>
								<TableCell>Amount</TableCell>
								<TableCell>Date</TableCell>
								<TableCell>Created by</TableCell>
								<TableCell align="center">View</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{Object.entries(invoices).map(([id, invoice]) => (
								<TableRow key={id}>
									<TableCell>
										{invoice.invoiceNumber}
									</TableCell>
									<TableCell>
										{invoice.customerName}
									</TableCell>
									<TableCell>
										₹{calculateTotal(invoice.items)}
									</TableCell>
									<TableCell>
										{formatDate(invoice.createdAt)}
									</TableCell>
									<TableCell>{invoice.username}</TableCell>
									<TableCell align="center">
										<Button
											variant="contained"
											size="small"
											onClick={() =>
												window.open(
													`/invoice?invoiceNumber=${invoice.invoiceNumber}`,
													"_blank"
												)
											}
											startIcon={<VisibilityIcon />}
										>
											View
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</>
	);
};
